import type { ReactNode } from 'react';
import { createContext, useState } from 'react';

import { minutesToTimeString, timeStringToMinutes } from '@repo/common';

import { defaultAvailability, defaultTimeRange } from '../constants/time';
import type { DayAvailability, TimeRange } from '../types/time';
import { isOverlapping } from '../utils/time';

export interface AvailabilityContextType {
  availability: Record<string, DayAvailability>;
  toggleDay: (day: string) => void;
  addTimeRange: (day: string) => void;
  removeTimeRange: (day: string, index: number) => void;
  updateTimeRange: (day: string, index: number, timeRange: TimeRange) => void;
  copyTimeRange: (sourceDay: string, sourceIndex: number, targetDays: string[]) => void;
  hasOverlap: (day: string, index: number) => boolean;
}

export const AvailabilityContext = createContext<AvailabilityContextType | undefined>(undefined);

interface AvailabilityProviderProps {
  initialAvailability?: Record<string, DayAvailability>;
  children: ReactNode;
}

const TIME_SLOT_DURATION = 60; // 1 hour in minutes
const START_OF_DAY = '00:00';
const END_OF_DAY = '23:59';

function findNextAvailableSlot(existingRanges: TimeRange[]): TimeRange {
  if (existingRanges.length === 0) {
    return {
      start: START_OF_DAY,
      end: minutesToTimeString(timeStringToMinutes(START_OF_DAY) + TIME_SLOT_DURATION),
    };
  }

  const sortedRanges = [...existingRanges].sort((a, b) => timeStringToMinutes(a.start) - timeStringToMinutes(b.start));
  const lastRange = sortedRanges[sortedRanges.length - 1];
  const startTime = timeStringToMinutes(lastRange.end);

  if (startTime + TIME_SLOT_DURATION <= timeStringToMinutes(END_OF_DAY)) {
    return {
      start: minutesToTimeString(startTime),
      end: minutesToTimeString(Math.min(startTime + TIME_SLOT_DURATION, timeStringToMinutes(END_OF_DAY))),
    };
  }

  // If no slot found after the last range, look for gaps between existing ranges
  for (let i = 0; i < sortedRanges.length - 1; i++) {
    const currentEnd = timeStringToMinutes(sortedRanges[i].end);
    const nextStart = timeStringToMinutes(sortedRanges[i + 1].start);

    if (nextStart - currentEnd >= TIME_SLOT_DURATION) {
      return {
        start: minutesToTimeString(currentEnd),
        end: minutesToTimeString(currentEnd + TIME_SLOT_DURATION),
      };
    }
  }

  return { start: START_OF_DAY, end: minutesToTimeString(TIME_SLOT_DURATION) };
}

function sortTimeRanges(ranges: TimeRange[]): TimeRange[] {
  return [...ranges].sort((a, b) => timeStringToMinutes(a.start) - timeStringToMinutes(b.start));
}

export function AvailabilityProvider({
  initialAvailability = defaultAvailability,
  children,
}: AvailabilityProviderProps) {
  const [availability, setAvailability] = useState<Record<string, DayAvailability>>(initialAvailability);

  const toggleDay = (day: string) => {
    setAvailability(prev => {
      const current = prev[day];
      const willBeAvailable = !current.isAvailable;
      // When enabling a day with no ranges, inject a default time range.
      const nextRanges = willBeAvailable && current.timeRanges.length === 0 ? [defaultTimeRange] : current.timeRanges;
      return {
        ...prev,
        [day]: {
          ...current,
          isAvailable: willBeAvailable,
          timeRanges: nextRanges,
        },
      };
    });
  };

  const addTimeRange = (day: string) => {
    setAvailability(prev => {
      const newTimeRange = findNextAvailableSlot(prev[day].timeRanges);
      return {
        ...prev,
        [day]: {
          ...prev[day],
          timeRanges: sortTimeRanges([...prev[day].timeRanges, newTimeRange]),
        },
      };
    });
  };

  const removeTimeRange = (day: string, index: number) => {
    setAvailability(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        timeRanges: sortTimeRanges(prev[day].timeRanges.filter((_, i) => i !== index)),
      },
    }));
  };

  const updateTimeRange = (day: string, index: number, timeRange: TimeRange) => {
    setAvailability(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        timeRanges: sortTimeRanges(prev[day].timeRanges.map((range, i) => (i === index ? timeRange : range))),
      },
    }));
  };

  const copyTimeRange = (sourceDay: string, sourceIndex: number, targetDays: string[]) => {
    setAvailability(prev => {
      const newAvailability = { ...prev };
      const sourceTimeRange = prev[sourceDay].timeRanges[sourceIndex];
      const duration = timeStringToMinutes(sourceTimeRange.end) - timeStringToMinutes(sourceTimeRange.start);

      targetDays.forEach(targetDay => {
        const newTimeRange = findNextAvailableSlot(newAvailability[targetDay].timeRanges);
        const endTime = minutesToTimeString(timeStringToMinutes(newTimeRange.start) + duration);
        newAvailability[targetDay] = {
          isAvailable: true,
          timeRanges: sortTimeRanges([
            ...newAvailability[targetDay].timeRanges,
            {
              start: newTimeRange.start,
              end: endTime,
            },
          ]),
        };
      });
      return newAvailability;
    });
  };

  const hasOverlap = (day: string, index: number) => {
    const timeRanges = availability[day].timeRanges;
    return timeRanges.some((r, i) => i !== index && isOverlapping(r, timeRanges[index]));
  };

  return (
    <AvailabilityContext.Provider
      value={{
        availability,
        toggleDay,
        addTimeRange,
        removeTimeRange,
        updateTimeRange,
        copyTimeRange,
        hasOverlap,
      }}
    >
      {children}
    </AvailabilityContext.Provider>
  );
}
